import React, { useContext } from 'react';
import { Navigate, useLocation } from "react-router-dom";
import { useIsAuthenticated } from "@azure/msal-react";

import { isInRole } from '../../utils/utilityFunctions.js';
import { MahiContext, mahiRolesFromMahiContext } from '../state/MahiContext.js';
import { getValidateMahiTokenSimplified } from '../../data/hikoFetch.js';
import { useState } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { useEffect } from 'react';
import { SignOutButton } from './SignoutButton.js';
import { LoadingBox } from '@northpowerdev/np-react-utils';

//
// Works like Route if user is logged in and has at least one of the specified roles.
// Otherwise redirects to login page.
// Handles a couple of tricky cases when routes are not known.
//
export default function ProtectedRoute({ children, roles = [] }) {

    const isUserAuthenticated = useIsAuthenticated()
    const { pathname, search } = useLocation()
    const mahiContext = useContext(MahiContext)
    const [isMahiTokenValid, setIsMahiTokenValid] = useState(false) 
    const [isMahiTokenRefreshing, setIsMahiTokenRefreshing] = useState(false) 

    // console.log('ProtectedRoute REFRESH')

    
    useEffect(() => {
        //
        // At start-up, or when user gets authenticated validate the user's Mahi token.
        //
        // console.log('ProtectedRoute useEffect')
        if (isUserAuthenticated && !isMahiTokenValid && !isMahiTokenRefreshing) {
            //
            // We're authenticated at Azure AD but haven't validated the Mahi token yet.
            // Call Mahi bff token validation web service and see if it succeeds.
            //
            // Might be able to avoid the web service call by checking token and roles (mahiToken && mahiRoles?.length > 0) plus checking the token expiry
            //
            // console.log('ProtectedRoute useEffect token not valid and refresh not in progress');
            (async () => {
                // console.log('ProtectedRoute useEffect setIsMahiTokenRefreshing true');
                setIsMahiTokenRefreshing(true);
                // console.log('ProtectedRoute useEffect calling getValidateMahiTokenSimplified');
                
                //
                // Use validate-mahi-token web service to check the token in the mahiContext
                // Does not attempt to refresh the token.
                //
                const tokenResponse = await getValidateMahiTokenSimplified(mahiContext);
                //
                // On success tokenResponse hold a valid Mahi token.
                // On error tokenResponse is null
                //
                // console.log('ProtectedRoute useEffect tokenResponse ', tokenResponse);
                // console.log('ProtectedRoute useEffect setIsMahiTokenValid ', tokenResponse ? 'true' : 'false');
                setIsMahiTokenValid(tokenResponse ? true : false); 
                // console.log('ProtectedRoute useEffect setIsMahiTokenRefreshing false');
                setIsMahiTokenRefreshing(false);
            })();
        }
    // eslint-disable-next-line
    }, [isUserAuthenticated, mahiContext]) // depends on isMahiTokenValid and isMahiContextRefreshing but don't want to refresh token if they change

    if (!isUserAuthenticated) {
        //
        // Not authenticated. Redirect to login page and expect that page to redirect back here when logged in.
        //
        const redirectPath = encodeURIComponent(`${pathname}${search}`);        
        return <Navigate to={`/login?redirect=${redirectPath}`} replace />;
    }

    //
    // User is authenticated with Azure AD and we have taken steps (via useEffect) to make sure their Mahi token is up to date.
    // Display something depending on token status and whether they have role permissions for this route.
    //
    let pageContent = null
    const mahiRoles = mahiRolesFromMahiContext(mahiContext)
    let isRouteAuthenticated = false
    if (isMahiTokenRefreshing) {
        pageContent = (
            <LoadingBox loading={isMahiTokenRefreshing} message="Getting Mahi Token">We're refreshing your Mahi token.</LoadingBox>
        )
    } else if (!isMahiTokenValid) {
        // Not refreshing token but the token is invalid 
        //
        // If we're sure this isn't some transient program state
        // we could automatically try to get a token via postAdminLogin (once anyway)
        //
        pageContent = (
            <Box>
                <Grid container direction='column' spacing={2} sx={{pt:3}} alignItems='flex-start' alignContent='center'>
                    <Typography variant="body1" paragraph>
                        Please logout and log back in.
                    </Typography>
                    <SignOutButton sx={{width:'10px'}} label="Logout"></SignOutButton>
                    <Typography variant="body1" color="secondary" sx={{mt:2}}>
                        If you're still having issues, <br/>please contact Network Platforms Team via <a href="mailto:helpdesk@northpower.com?subject=Mahi%20roles%20to%20be%20setup&body=Hi%20Helpdesk,%20Could%20you%20please%20get%20the%20Network%20Platform%20Team%20to%20setup%20access%20in%20Mahi%20for%20me.">Helpdesk</a> to set you up with the correct Mahi roles.
                    </Typography>
                </Grid>
            </Box>
        )
    } else {
        //
        // We're not refreshing the Mahi token and the Mahi token is valid.
        // ASSUMPTION: Logged in users have at least one role.
        //
        // const mahiToken = mahiTokenFromMahiContext(mahiContext)
        if (mahiRoles && mahiRoles.length) {
            //
            // We're authenticated and we know at least some user roles.
            //
            // Work out if user is authorised for this route.
            // User is authorised for this route if their userRoles contains
            // at least one of the roles required by the route, or if the route
            // doesn't require any roles.
            //
            isRouteAuthenticated = (!roles.length || isInRole(mahiRoles, roles)) // true or false
        }

        //
        // User is authenticated and we have an opinion about their authorisation for this route.
        // We use isRouteAuthenticated to work out what to do next.
        // Possible values are: undefined, false, true.
        //
        if (!isRouteAuthenticated) {
            //
            // User is authenticated and has at least one role.
            // Unfortunately they are not in a role that allows them to see this route.
            // Don't show the route.
            //
            // console.log("ProtectedRoute: not authorised. Redirecting to home page.")
            pageContent = (
                <>
                <Box>
                    <Grid container spacing={2} sx={{pt:3}} justifyContent={'center'}>
                        <Typography variant="body1" color="secondary">                    
                            You're not authorised to see this page. <br/>Please contact Network Platforms Team via <a href="mailto:helpdesk@northpower.com?subject=Mahi%20roles%20to%20be%20setup&body=Hi%20Helpdesk,%20Could%20you%20please%20get%20the%20Network%20Platform%20Team%20to%20setup%20access%20in%20Mahi%20for%20me.">Helpdesk</a> to set you up with the correct Mahi roles.
                        </Typography>
                    </Grid>
                </Box>
            </>
            )
        } else {
            //
            // User is authenticated, we know their roles and they are authorised to view this route.
            // Show the route.
            //
            pageContent = children
        }
    }

    return pageContent

    // return (
    //     <>
    //         <Box>
    //             <Typography variant="body1">This is the ProtectedRoute component</Typography>
    //             {
    //                 pageContent
    //             }
    //         </Box>
    //         <Box>
    //             <DebugBox sx={{mt:2}} title="isUserAuthenticated">{isUserAuthenticated ? 'true' : 'false'}</DebugBox>
    //             <DebugBox title="isMahiTokenRefreshing">{isMahiTokenRefreshing ? 'true' : 'false'}</DebugBox>
    //             <DebugBox title="isMahiTokenValid">{isMahiTokenValid ? 'true' : 'false'}</DebugBox>
    //             <DebugBox title="isRouteAuthenticated">{isRouteAuthenticated ? 'true' : 'false'}</DebugBox>
    //             <DebugBox title="mahiRoles" stringify>{mahiRoles}</DebugBox>
    //             {/* <DebugBox title="mahiContext" stringify>{mahiContext}</DebugBox> */}
    //         </Box>
    //     </>
    // )
}
